/**
 * Utility functions for extracting position data from scoresheet logs
 */

export interface PositionCoordinate {
  x: number;
  y: number;
}

export interface PositionExtractionResult {
  position_start: PositionCoordinate | null;
  position_end: PositionCoordinate | null;
}

/**
 * Extract first and last coordinates from position_data JSON array
 * @param positionData - JSON array of {x, y} coordinates or null
 * @returns Object with position_start and position_end (null if no data)
 */
export function extractPositionStartEnd(positionData: any): PositionExtractionResult {
  // If position_data is null, undefined, or not an array
  if (!positionData || !Array.isArray(positionData) || positionData.length === 0) {
    return {
      position_start: null,
      position_end: null
    };
  }

  // Get first coordinate
  const firstCoord = positionData[0];
  // Get last coordinate
  const lastCoord = positionData[positionData.length - 1];

  // Validate coordinates have x and y
  const positionStart: PositionCoordinate | null = 
    (firstCoord && typeof firstCoord.x === 'number' && typeof firstCoord.y === 'number')
      ? { x: firstCoord.x, y: firstCoord.y }
      : null;

  const positionEnd: PositionCoordinate | null = 
    (lastCoord && typeof lastCoord.x === 'number' && typeof lastCoord.y === 'number')
      ? { x: lastCoord.x, y: lastCoord.y }
      : null;

  // If only one coordinate exists, use it for both
  if (positionStart && !positionEnd && positionData.length === 1) {
    return {
      position_start: positionStart,
      position_end: positionStart
    };
  }

  return {
    position_start: positionStart,
    position_end: positionEnd
  };
}

/**
 * Check if position_data has valid coordinates
 * @param positionData - JSON array of coordinates, single coordinate object, or null
 * @returns true if position_data exists and has at least one valid coordinate
 */
export function hasValidPositionData(positionData: any): boolean {
  if (!positionData) {
    return false;
  }

  // Handle single coordinate object format: {x: number, y: number}
  if (typeof positionData === 'object' && !Array.isArray(positionData)) {
    return typeof positionData.x === 'number' && typeof positionData.y === 'number';
  }

  // Handle array format: [{x: number, y: number}, ...]
  if (Array.isArray(positionData)) {
    if (positionData.length === 0) {
      return false;
    }
    // Check if at least one coordinate is valid
    return positionData.some((coord: any) => 
      coord && typeof coord.x === 'number' && typeof coord.y === 'number'
    );
  }

  return false;
}


